import React, { useCallback, useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { SxProps, Theme } from "@mui/material/styles";
import { LightTooltip } from "../../../LightTooltip";
import { InstallAgeFilter, InstallAgeValue } from "./InstallAgeFilter";

interface InstallAgeSectionProps {
  disabled?: boolean;
  value: InstallAgeValue;
  onChange: (value: InstallAgeValue) => void;
  filterHasSelection: boolean;
  sx?: SxProps<Theme>;
}

const TOOLTIP_TEXT = "Filter companies based on the age of product installs.";

export const InstallAgeSection: React.FC<InstallAgeSectionProps> = React.memo(
  ({ disabled = false, value, onChange, filterHasSelection, sx }) => {
    const hasInstallAgeValue =
      value.min !== undefined || value.max !== undefined;
    const [installAgeEnabled, setInstallAgeEnabled] =
      useState(hasInstallAgeValue);

    useEffect(() => {
      if (hasInstallAgeValue) {
        setInstallAgeEnabled(true);
      }

      if (hasInstallAgeValue === false && disabled) {
        setInstallAgeEnabled(false);
      }
    }, [hasInstallAgeValue, disabled]);

    const handleInstallAgeToggle = useCallback(() => {
      setInstallAgeEnabled((prev) => {
        const newEnabled = !prev;

        if (!newEnabled) {
          onChange({ min: undefined, max: undefined });
        }

        return newEnabled;
      });
    }, [onChange]);

    return (
      <Box sx={sx}>
        <Stack direction="row" alignItems="center">
          <FormControlLabel
            sx={{ mr: 1 }}
            control={
              <Checkbox
                checked={installAgeEnabled}
                onChange={handleInstallAgeToggle}
                disabled={disabled || !filterHasSelection}
                size="small"
              />
            }
            label="Refine by Install Age"
          />
          <LightTooltip title={TOOLTIP_TEXT} placement="top">
            <InfoOutlineIcon
              fontSize="small"
              sx={{ color: "text.secondary" }}
            />
          </LightTooltip>
        </Stack>

        {installAgeEnabled && !disabled && filterHasSelection && (
          <Box mt={1}>
            <InstallAgeFilter
              value={value}
              onChange={onChange}
              disabled={disabled}
            />
          </Box>
        )}
      </Box>
    );
  }
);

InstallAgeSection.displayName = "InstallAgeSection";
